import React, { useState, useCallback, useMemo, useRef } from 'react';
import { FiSearch, FiX, FiChevronDown } from 'react-icons/fi';
import { PiBuildings, PiMapPin, PiUser, PiPackage, PiHash, PiCalendarBlank } from 'react-icons/pi';
import { TagFilter } from '../../schemas/boolean_filtering';
import { EntityType } from '../../routines/schemas';
import {
    RelevanceLevel,
    RelevanceRanges,
    getLabelFromScoreRange,
    EntityPresenceLevel,
    EntityPresenceRanges,
    getEntityPresenceLevelFromScoreRange,
} from '../../routines/schemas';
import { RelevanceLevelColors } from '../../colors';

// Colors for entity presence levels
const EntityPresenceColors: Record<EntityPresenceLevel, string> = {
    Include: '#22c55e', // green-500
    Exclude: '#ef4444', // red-500
};
import { DropdownPortal } from './dropdown-portal';

// Entity styling (matches in-grid-codes-view.tsx)
const entityColors: Record<EntityType, string> = {
    [EntityType.organization]: "bg-emerald-100 text-emerald-800 border-emerald-200",
    [EntityType.location]: "bg-amber-100 text-amber-800 border-amber-200",
    [EntityType.item]: "bg-violet-100 text-violet-800 border-violet-200",
    [EntityType.topic]: "bg-cyan-100 text-cyan-800 border-cyan-200",
    [EntityType.person]: "bg-indigo-100 text-indigo-800 border-indigo-200",
    [EntityType.event]: "bg-slate-100 text-slate-800 border-slate-200",
};

const entityIcons: Record<EntityType, React.ReactNode> = {
    [EntityType.organization]: <PiBuildings className="w-3 h-3 text-emerald-600" />,
    [EntityType.location]: <PiMapPin className="w-3 h-3 text-amber-600" />,
    [EntityType.item]: <PiPackage className="w-3 h-3 text-violet-600" />,
    [EntityType.topic]: <PiHash className="w-3 h-3 text-cyan-600" />,
    [EntityType.person]: <PiUser className="w-3 h-3 text-indigo-600" />,
    [EntityType.event]: <PiCalendarBlank className="w-3 h-3 text-slate-600" />,
};

export type TagOptionWithMeta = {
    id: string;
    name: string;
    isCurrentChat?: boolean;
    chatName?: string;
    entityType?: EntityType;
    /** True for transcript exchange tags (Admissions, Denials, etc.) */
    isTranscript?: boolean;
    /** Number of documents associated with this tag */
    docCount?: number;
};

interface TagFilterDropdownProps {
    /** Available tags to filter on */
    options: TagOptionWithMeta[];
    /** Current tag filters (map of tag_id -> TagFilter[]) */
    tagFilters: Map<string, TagFilter[]> | null;
    /** Callback to update tag filters */
    setTagFilters: React.Dispatch<React.SetStateAction<Map<string, TagFilter[]> | null>>;
    /** Placeholder text */
    placeholder?: string;
}

/**
 * A single selected filter row with Hot/Warm/Cold buttons (or Include/Exclude for entities)
 */
const SelectedFilterRow: React.FC<{
    option: TagOptionWithMeta;
    activeFilters: TagFilter[];
    onToggleLevel: (tagId: string, level: RelevanceLevel) => void;
    onToggleEntityLevel: (tagId: string, level: EntityPresenceLevel) => void;
    onRemove: (tagId: string) => void;
}> = ({ option, activeFilters, onToggleLevel, onToggleEntityLevel, onRemove }) => {
    const isEntity = !!option.entityType;
    const entityStyle = option.entityType ? entityColors[option.entityType] : '';

    // For entities, use Include/Exclude; for regular tags, use Hot/Warm/Cold
    const activeRelevanceLevels = useMemo(() => {
        if (isEntity) return [];
        return activeFilters
            .map((f) => getLabelFromScoreRange(f.min_score, f.max_score))
            .filter(Boolean) as RelevanceLevel[];
    }, [activeFilters, isEntity]);

    const activeEntityLevels = useMemo(() => {
        if (!isEntity) return [];
        return activeFilters
            .map((f) => getEntityPresenceLevelFromScoreRange(f.min_score, f.max_score))
            .filter(Boolean) as EntityPresenceLevel[];
    }, [activeFilters, isEntity]);

    const relevanceLevels: RelevanceLevel[] = ['Hot', 'Warm', 'Cold'];
    const entityLevels: EntityPresenceLevel[] = ['Include', 'Exclude'];

    return (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border shadow-sm ${
            isEntity && entityStyle ? entityStyle : 'bg-white border-[#a1a1b6]'
        }`}>
            {/* Entity icon */}
            {isEntity && option.entityType && entityIcons[option.entityType]}

            {/* Tag name + doc count */}
            <div className="flex-1 min-w-0">
                <span className={`text-sm font-medium truncate block ${isEntity ? '' : 'text-[#1c1c24]'}`} title={option.name}>
                    {option.name}
                    {option.docCount != null && option.docCount > 0 && (
                        <span className="text-[10px] text-[#8c8ca3] font-normal ml-1.5">({option.docCount})</span>
                    )}
                </span>
                {option.isCurrentChat && (
                    <span className="text-[10px] text-[#8c8ca3] font-medium">(current chat)</span>
                )}
            </div>

            {/* Filter buttons - different for entities vs regular tags */}
            <div className="flex items-center gap-1">
                {isEntity ? (
                    // Entity presence buttons: Include / Exclude
                    entityLevels.map((level) => {
                        const isActive = activeEntityLevels.includes(level);
                        const bgColor = isActive ? EntityPresenceColors[level] : 'transparent';
                        const textColor = isActive ? '#fff' : '#6d6d8a';
                        const borderColor = isActive ? bgColor : '#a1a1b6';

                        return (
                            <button
                                key={level}
                                onClick={() => onToggleEntityLevel(option.id, level)}
                                className="px-2.5 py-1 rounded-md text-xs font-medium transition-all border hover:opacity-80"
                                style={{
                                    backgroundColor: bgColor,
                                    color: textColor,
                                    borderColor: borderColor,
                                }}
                                title={`${isActive ? 'Remove' : 'Add'} ${level} filter`}
                            >
                                {level}
                            </button>
                        );
                    })
                ) : (
                    // Relevance buttons: Hot / Warm / Cold
                    relevanceLevels.map((level) => {
                        const isActive = activeRelevanceLevels.includes(level);
                        const bgColor = isActive ? RelevanceLevelColors[level] : 'transparent';
                        const textColor = isActive ? '#fff' : '#6d6d8a';
                        const borderColor = isActive ? bgColor : '#a1a1b6';

                        return (
                            <button
                                key={level}
                                onClick={() => onToggleLevel(option.id, level)}
                                className="px-2.5 py-1 rounded-md text-xs font-medium transition-all border hover:opacity-80"
                                style={{
                                    backgroundColor: bgColor,
                                    color: textColor,
                                    borderColor: borderColor,
                                }}
                                title={`${isActive ? 'Remove' : 'Add'} ${level} filter`}
                            >
                                {level}
                            </button>
                        );
                    })
                )}
            </div>

            {/* Remove button */}
            <button
                onClick={() => onRemove(option.id)}
                className="p-1 text-[#6d6d8a] hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Remove filter"
            >
                <FiX className="w-[1em] h-[1em]" />
            </button>
        </div>
    );
};

/**
 * A searchable dropdown that allows selecting tags to filter on.
 * Selected tags appear as rows with Hot/Warm/Cold toggle buttons.
 */
export const TagFilterDropdown: React.FC<TagFilterDropdownProps> = ({
    options,
    tagFilters,
    setTagFilters,
    placeholder = "Search and add filters...",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const triggerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Get selected tag IDs
    const selectedTagIds = useMemo(() => {
        if (!tagFilters) return new Set<string>();
        const ids = new Set<string>();
        for (const tagId of tagFilters.keys()) {
            if (options.some((opt) => opt.id === tagId)) {
                ids.add(tagId);
            }
        }
        return ids;
    }, [tagFilters, options]);

    // Selected options (shown as rows)
    const selectedOptions = useMemo(() => {
        return options.filter((opt) => selectedTagIds.has(opt.id));
    }, [options, selectedTagIds]);

    // Available options for dropdown (not yet selected), filtered by search
    const availableOptions = useMemo(() => {
        const unselected = options.filter((opt) => !selectedTagIds.has(opt.id));
        if (!searchQuery.trim()) {
            return unselected;
        }
        const query = searchQuery.toLowerCase();
        return unselected.filter((opt) => opt.name.toLowerCase().includes(query));
    }, [options, selectedTagIds, searchQuery]);

    // Sort: current chat items first, then by entity type (if present), then alphabetical
    const sortedAvailableOptions = useMemo(() => {
        return [...availableOptions].sort((a, b) => {
            // Current chat items first
            if (a.isCurrentChat && !b.isCurrentChat) return -1;
            if (!a.isCurrentChat && b.isCurrentChat) return 1;
            // Then group by entity type
            const typeCompare = (a.entityType ?? '').localeCompare(b.entityType ?? '');
            if (typeCompare !== 0) return typeCompare;
            // Then alphabetical
            return a.name.localeCompare(b.name);
        });
    }, [availableOptions]);

    // Group options by entity type or transcript status for display
    const groupedOptions = useMemo(() => {
        const hasEntityTypes = sortedAvailableOptions.some(opt => opt.entityType);
        const hasTranscript = sortedAvailableOptions.some(opt => opt.isTranscript);
        if (!hasEntityTypes && !hasTranscript) return null;

        const groups = new Map<string, TagOptionWithMeta[]>();
        for (const opt of sortedAvailableOptions) {
            const key = opt.isTranscript ? '_testimony' : (opt.entityType ?? '_other');
            const group = groups.get(key) ?? [];
            group.push(opt);
            groups.set(key, group);
        }
        // Order: Workspace Codes → entity types → Testimony
        const ordered = new Map<string, TagOptionWithMeta[]>();
        if (groups.has('_other')) ordered.set('_codes', groups.get('_other')!);
        for (const [key, val] of groups) {
            if (key !== '_other' && key !== '_testimony') ordered.set(key, val);
        }
        if (groups.has('_testimony')) ordered.set('_testimony', groups.get('_testimony')!);
        return ordered;
    }, [sortedAvailableOptions]);

    // Add tag with appropriate default filter (Hot for regular tags, Include for entities)
    const handleSelectTag = useCallback((tagId: string) => {
        const option = options.find(o => o.id === tagId);
        const isEntity = !!option?.entityType;

        // Use Include for entities, Hot for regular tags
        const { min, max } = isEntity
            ? EntityPresenceRanges['Include']
            : RelevanceRanges['Hot'];

        setTagFilters((prev) => {
            const next = new Map(prev ?? []);
            next.set(tagId, [{ tag_id: tagId, min_score: min, max_score: max }]);
            return next;
        });
        setSearchQuery('');
        setIsOpen(false);
    }, [setTagFilters, options]);

    // Toggle relevance level (for regular tags)
    const handleToggleLevel = useCallback((tagId: string, level: RelevanceLevel) => {
        setTagFilters((prev) => {
            const next = new Map(prev ?? []);
            const filters = next.get(tagId) ?? [];

            const { min, max } = RelevanceRanges[level];
            const existingIdx = filters.findIndex(
                (f) => getLabelFromScoreRange(f.min_score, f.max_score) === level
            );

            if (existingIdx >= 0) {
                // Remove this level
                const newFilters = filters.filter((_, i) => i !== existingIdx);
                if (newFilters.length === 0) {
                    next.delete(tagId);
                } else {
                    next.set(tagId, newFilters);
                }
            } else {
                // Add this level
                next.set(tagId, [...filters, { tag_id: tagId, min_score: min, max_score: max }]);
            }

            return next.size > 0 ? next : null;
        });
    }, [setTagFilters]);

    // Toggle entity presence level (Include/Exclude for entity tags)
    const handleToggleEntityLevel = useCallback((tagId: string, level: EntityPresenceLevel) => {
        setTagFilters((prev) => {
            const next = new Map(prev ?? []);
            const filters = next.get(tagId) ?? [];

            const { min, max } = EntityPresenceRanges[level];
            const existingIdx = filters.findIndex(
                (f) => getEntityPresenceLevelFromScoreRange(f.min_score, f.max_score) === level
            );

            if (existingIdx >= 0) {
                // Remove this level
                const newFilters = filters.filter((_, i) => i !== existingIdx);
                if (newFilters.length === 0) {
                    next.delete(tagId);
                } else {
                    next.set(tagId, newFilters);
                }
            } else {
                // Add this level (for entities, typically only one level at a time)
                // Replace existing filters for mutual exclusivity
                next.set(tagId, [{ tag_id: tagId, min_score: min, max_score: max }]);
            }

            return next.size > 0 ? next : null;
        });
    }, [setTagFilters]);

    // Remove tag filter
    const handleRemoveTag = useCallback((tagId: string) => {
        setTagFilters((prev) => {
            if (!prev) return null;
            const next = new Map(prev);
            next.delete(tagId);
            return next.size > 0 ? next : null;
        });
    }, [setTagFilters]);

    return (
        <div className="space-y-2">
            {/* Selected filters as rows */}
            {selectedOptions.length > 0 && (
                <div className="space-y-1.5">
                    {selectedOptions.map((opt) => (
                        <SelectedFilterRow
                            key={opt.id}
                            option={opt}
                            activeFilters={tagFilters?.get(opt.id) ?? []}
                            onToggleLevel={handleToggleLevel}
                            onToggleEntityLevel={handleToggleEntityLevel}
                            onRemove={handleRemoveTag}
                        />
                    ))}
                </div>
            )}

            {/* Searchable dropdown */}
            <div className="relative">
                <div
                    ref={triggerRef}
                    className={`flex items-center gap-2 px-3 py-2 bg-white rounded-lg border transition-colors cursor-pointer ${
                        isOpen ? 'border-[#8c8ca3] ring-2 ring-[#ececf3]' : 'border-[#a1a1b6] hover:border-[#8c8ca3]'
                    }`}
                    onClick={() => {
                        setIsOpen(true);
                        setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                >
                    <FiSearch className="text-[#6d6d8a] w-[1em] h-[1em]" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsOpen(true)}
                        placeholder={placeholder}
                        className="flex-1 bg-transparent outline-none text-sm text-[#1c1c24] placeholder-[#6d6d8a]"
                    />
                    <FiChevronDown
                        className={`text-[#6d6d8a] transition-transform w-[1em] h-[1em] ${isOpen ? 'rotate-180' : ''}`}
                    />
                </div>

                {/* Dropdown options - rendered via portal */}
                <DropdownPortal
                    triggerRef={triggerRef}
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    maxHeight={240}
                >
                    {sortedAvailableOptions.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-[#6d6d8a] text-center">
                            {searchQuery ? 'No matching items' : 'All items selected'}
                        </div>
                    ) : groupedOptions ? (
                        // Grouped rendering for entity types
                        Array.from(groupedOptions.entries()).map(([type, opts]) => (
                            <div key={type}>
                                {(
                                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#8c8ca3] bg-[#f7f7fa] border-b border-[#ececf3] flex items-center gap-1.5">
                                        {type === '_codes' ? (
                                            <span>Workspace Codes</span>
                                        ) : type === '_testimony' ? (
                                            <span>Testimony Codes</span>
                                        ) : (
                                            <>
                                                {entityIcons[type as EntityType]}
                                                <span>{type}</span>
                                            </>
                                        )}
                                    </div>
                                )}
                                {opts.map((opt) => (
                                    <button
                                        key={opt.id}
                                        onClick={() => handleSelectTag(opt.id)}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-[#ececf3] transition-colors flex items-center gap-2 group"
                                    >
                                        {opt.entityType && entityIcons[opt.entityType]}
                                        <span className="text-[#1c1c24] truncate flex-1">{opt.name}</span>
                                        {opt.docCount != null && opt.docCount > 0 && (
                                            <span className="text-[10px] text-[#8c8ca3] tabular-nums">{opt.docCount}</span>
                                        )}
                                        {opt.isCurrentChat && (
                                            <span className="text-[10px] text-[#8c8ca3] font-medium bg-[#ececf3] px-1.5 py-0.5 rounded">
                                                current chat
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        ))
                    ) : (
                        // Flat rendering for non-entity options
                        sortedAvailableOptions.map((opt) => (
                            <button
                                key={opt.id}
                                onClick={() => handleSelectTag(opt.id)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-[#ececf3] transition-colors flex items-center gap-2 group"
                            >
                                <span className="text-[#1c1c24] truncate flex-1">{opt.name}</span>
                                {opt.docCount != null && opt.docCount > 0 && (
                                    <span className="text-[10px] text-[#8c8ca3] tabular-nums">{opt.docCount}</span>
                                )}
                                {opt.isCurrentChat && (
                                    <span className="text-[10px] text-[#8c8ca3] font-medium bg-[#ececf3] px-1.5 py-0.5 rounded">
                                        current chat
                                    </span>
                                )}
                            </button>
                        ))
                    )}
                </DropdownPortal>
            </div>

            {/* Empty state */}
            {selectedOptions.length === 0 && !isOpen && (
                <div className="text-xs text-[#6d6d8a] italic">
                    Click to add filters
                </div>
            )}
        </div>
    );
};

export default TagFilterDropdown;
